import React from 'react';
import { 
  Box, 
  IconButton, 
  Tooltip,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  FormGroup,
  FormControlLabel,
  Switch,
  Button
} from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import BuildIcon from '@mui/icons-material/Build';
import HomeIcon from '@mui/icons-material/Home';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';

interface SidebarProps {
  activeSection: 'apps' | 'tools' | 'docs' | null;
  onSectionChange: (section: 'apps' | 'tools' | 'docs' | null) => void;
  appCount?: number;
  // These props are missing in your interface but you're passing them
  isContentAreaVisible: boolean;
  setIsContentAreaVisible: (visible: boolean) => void;
  disabledPanels: {
    leftPanel: boolean;
    topRightPanel: boolean;
    bottomRightPanel: boolean;
  };
  setDisabledPanels: (panels: {
    leftPanel: boolean;
    topRightPanel: boolean;
    bottomRightPanel: boolean;
  }) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  activeSection, 
  onSectionChange,
  appCount = 0,
  // Add the new props
  isContentAreaVisible,
  setIsContentAreaVisible,
  disabledPanels,
  setDisabledPanels
}) => {
  // Add state for panel controls dialog
  const [showPanelControls, setShowPanelControls] = React.useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '60px',
        height: '100%',
        backgroundColor: '#202124',
        color: 'white',
        py: 2
      }}
    >
      {/* Existing sidebar content */}
      <Divider sx={{ width: '80%', backgroundColor: 'rgba(255,255,255,0.1)', my: 1 }} />

      {/* Home button */}
      <Tooltip title="Home" placement="right">
        <IconButton
          onClick={() => onSectionChange(null)}
          sx={{
            color: 'white',
            bgcolor: activeSection === null ? 'rgba(255,255,255,0.15)' : 'transparent',
            my: 1,
            opacity: activeSection === null ? 1 : 0.7,
            '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <HomeIcon />
        </IconButton>
      </Tooltip>

      <Divider sx={{ width: '80%', backgroundColor: 'rgba(255,255,255,0.1)', my: 2 }} />

      {/* Main navigation buttons */}
      <Tooltip title="Applications" placement="right">
        <IconButton
          onClick={() => onSectionChange('apps')}
          sx={{
            color: 'white',
            bgcolor: activeSection === 'apps' ? 'rgba(255,255,255,0.15)' : 'transparent',
            my: 1,
            opacity: activeSection === 'apps' ? 1 : 0.7,
            '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <AppsIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Tools" placement="right">
        <IconButton
          onClick={() => onSectionChange('tools')}
          sx={{
            color: 'white',
            bgcolor: activeSection === 'tools' ? 'rgba(255,255,255,0.15)' : 'transparent',
            my: 1,
            opacity: activeSection === 'tools' ? 1 : 0.7,
            '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <BuildIcon />
        </IconButton>
      </Tooltip>

      {/* Spacer that pushes content to the bottom */}
      <Box sx={{ flexGrow: 1 }} />

      {/* First divider for bottom section */}
      <Divider sx={{ width: '80%', backgroundColor: 'rgba(255,255,255,0.1)', my: 2 }} />

      {/* Documentation button - moved down to be near Panel Controls */}
      <Tooltip title="Documentation" placement="right">
        <IconButton
          onClick={() => onSectionChange('docs')}
          sx={{
            color: 'white',
            bgcolor: activeSection === 'docs' ? 'rgba(255,255,255,0.15)' : 'transparent',
            my: 1,
            opacity: activeSection === 'docs' ? 1 : 0.7,
            '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <MenuBookIcon />
        </IconButton>
      </Tooltip>

      {/* Small divider between Documentation and Panel Controls */}
      <Divider sx={{ width: '60%', backgroundColor: 'rgba(255,255,255,0.08)', my: 1 }} />

      {/* Panel Controls button */}
      <Tooltip title="Panel Controls" placement="right">
        <IconButton
          onClick={() => setShowPanelControls(true)}
          sx={{
            color: 'white',
            bgcolor: showPanelControls ? 'rgba(255,255,255,0.15)' : 'transparent',
            my: 1,
            opacity: showPanelControls ? 1 : 0.7,
            '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>

      {/* Panel Controls Dialog */}
      <Dialog
        open={showPanelControls}
        onClose={() => setShowPanelControls(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: 'auto',
            maxWidth: '90vw',
            minWidth: 280
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" color="primary.main">Panel Controls</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <FormGroup>
            {/* App List Visibility Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={isContentAreaVisible}
                  onChange={() => setIsContentAreaVisible(!isContentAreaVisible)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Show Content Panel</Typography>}
            />
            {/* App List Disable Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={!disabledPanels.leftPanel}
                  onChange={() =>
                    setDisabledPanels({
                      ...disabledPanels,
                      leftPanel: !disabledPanels.leftPanel,
                    })
                  }
                  size="small"
                />
              }
              label={<Typography variant="body2">Enable Content Panel</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!disabledPanels.topRightPanel}
                  onChange={() =>
                    setDisabledPanels({
                      ...disabledPanels,
                      topRightPanel: !disabledPanels.topRightPanel,
                    })
                  }
                  size="small"
                />
              }
              label={<Typography variant="body2">Enable App Tester</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!disabledPanels.bottomRightPanel}
                  onChange={() =>
                    setDisabledPanels({
                      ...disabledPanels,
                      bottomRightPanel: !disabledPanels.bottomRightPanel,
                    })
                  }
                  size="small"
                />
              }
              label={<Typography variant="body2">Enable Token Panel</Typography>}
            />
          </FormGroup>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() =>
                setDisabledPanels({
                  leftPanel: false,
                  topRightPanel: false,
                  bottomRightPanel: false,
                })
              }
            >
              Reset All
            </Button>
            <Button 
              variant="contained" 
              size="small" 
              color="primary"
              onClick={() => setShowPanelControls(false)}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Sidebar;